import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import {
  EditorContent,
  JSONContent,
  ReactRenderer,
  useEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect, useRef } from 'react';
import tippy, {
  GetReferenceClientRect,
  Instance as TippyInstance,
} from 'tippy.js';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGetStaffUsersQuery } from '@/services/api/userService';

import { TaskUpdateMentionList } from '../tasks/task-detail/TaskUpdateMentionList';

interface CommentEditorProps {
  initialContent?: string;
  onSave: (content: string) => void | Promise<void>;
  onCancel: () => void;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  /** When provided, the draft is persisted to sessionStorage under this key so
   *  content survives component unmounts, polling re-renders and page refreshes. */
  draftKey?: string;
}

export const CommentEditor = ({
  initialContent = '',
  onSave,
  onCancel,
  className,
  placeholder = 'Add a note...',
  autoFocus = false,
  draftKey,
}: CommentEditorProps) => {
  const { data: staffUsers = [] } = useGetStaffUsersQuery();
  const staffUsersRef = useRef(staffUsers);

  useEffect(() => {
    staffUsersRef.current = staffUsers;
  }, [staffUsers]);

  // ── Draft persistence helpers ──────────────────────────────────────
  const readDraft = useCallback((): string => {
    if (!draftKey) return '';
    try {
      return sessionStorage.getItem(draftKey) ?? '';
    } catch {
      return '';
    }
  }, [draftKey]);

  const writeDraft = useCallback(
    (text: string) => {
      if (!draftKey) return;
      try {
        if (text) {
          sessionStorage.setItem(draftKey, text);
        } else {
          sessionStorage.removeItem(draftKey);
        }
      } catch {
        // sessionStorage may be full or unavailable — ignore
      }
    },
    [draftKey],
  );

  const clearDraft = useCallback(() => {
    if (!draftKey) return;
    try {
      sessionStorage.removeItem(draftKey);
    } catch {
      // ignore
    }
  }, [draftKey]);

  // Resolve initial content: prefer a persisted draft over the prop
  const resolvedInitial = draftKey ? readDraft() || initialContent : initialContent;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Mention.configure({
        HTMLAttributes: {
          class:
            'mention inline-flex items-center rounded-md bg-blue-50 px-1 py-0.5 pb-1 leading-none tracking-none text-blue-600 font-medium',
        },
        renderLabel: ({ node }) => {
          // Render as @username
          return `@${node.attrs.label}`;
        },
        suggestion: {
          char: '@',
          items: ({ query }) => {
            const users = staffUsersRef.current;
            const lowerQuery = query.toLowerCase();
            return users
              .filter((user) => {
                const fullName =
                  `${user.first_name} ${user.last_name}`.toLowerCase();
                const username = user.username.toLowerCase();
                const email = user.email.toLowerCase();
                return (
                  fullName.includes(lowerQuery) ||
                  username.includes(lowerQuery) ||
                  email.includes(lowerQuery)
                );
              })
              .slice(0, 10)
              .map((user) => ({
                id: user.id,
                label: user.username, // Username for display and what we want in the text
                value: `${user.first_name} ${user.last_name}`.trim(), // Full name as secondary info
              }));
          },
          render: () => {
            let component: ReactRenderer<any>;
            let popup: TippyInstance[];

            return {
              onStart: (props) => {
                component = new ReactRenderer(TaskUpdateMentionList, {
                  props,
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                popup = tippy('body', {
                  getReferenceClientRect:
                    props.clientRect as GetReferenceClientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },

              onUpdate: (props) => {
                component.updateProps(props);

                if (!props.clientRect) {
                  return;
                }

                popup[0].setProps({
                  getReferenceClientRect:
                    props.clientRect as GetReferenceClientRect,
                });
              },

              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }
                return component.ref?.onKeyDown(props);
              },

              onExit: () => {
                popup[0].destroy();
                component.destroy();
              },
            };
          },
        },
      }),
      // Configure Placeholder
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: React.useMemo(() => {
      if (!resolvedInitial) {
        return {
          type: 'doc',
          content: [{ type: 'paragraph', content: [] }],
        };
      }

      // Normalize line breaks:
      // 1. Handle Windows style (\r\n)
      // 2. Collapse default TipTap double newlines (\n\n) to single (\n) to prevent extra gaps
      const normalizedContent = resolvedInitial
        .replace(/\r\n/g, '\n')
        .replace(/\n\n/g, '\n');

      return {
        type: 'doc',
        content: normalizedContent.split('\n').map((line) => {
          if (!line) {
            return { type: 'paragraph' };
          }

          const parts = line.split(/(@[a-zA-Z0-9_]+(?:[.\-@][a-zA-Z0-9_]+)*)/g);
          const lineContent = parts
            .map((part) => {
              if (part.match(/^@[a-zA-Z0-9_]+(?:[.\-@][a-zA-Z0-9_]+)*$/)) {
                return {
                  type: 'mention',
                  attrs: { label: part.substring(1) },
                };
              }
              if (!part) return null;
              return { type: 'text', text: part };
            })
            .filter(Boolean) as JSONContent[];

          return {
            type: 'paragraph',
            content: lineContent,
          };
        }),
      };
    }, [resolvedInitial]),
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none min-h-[40px] px-2.5 py-2 text-[11px] leading-normal text-gray-900 [&_p]:mb-1 [&_p:last-child]:mb-0 font-medium',
          className,
        ),
      },
    },
    onUpdate: ({ editor: editorInstance }) => {
      // Persist draft on every keystroke (plain-text, cheap)
      writeDraft(editorInstance.getText({ blockSeparator: '\n' }));
    },
    onCreate: ({ editor: editorInstance }) => {
      if (autoFocus) {
        editorInstance.commands.focus();
      }
    },
  });

  // Handle Ctrl+Enter to save
  useEffect(() => {
    if (!editor) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    };

    const element = editor.view.dom;
    element.addEventListener('keydown', handleKeyDown);
    return () => element.removeEventListener('keydown', handleKeyDown);
  }, [editor]);

  const handleSave = async () => {
    if (editor && !editor.isEmpty) {
      try {
        await onSave(editor.getText({ blockSeparator: '\n' }));
        // Only clear content + draft after a successful save
        editor.commands.clearContent();
        clearDraft();
      } catch {
        // Save failed — keep the content in the editor so the user can retry
      }
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-col w-full bg-white rounded-md border border-gray-200 overflow-hidden relative z-10">
      <div className="max-h-[300px] overflow-y-auto">
        <EditorContent editor={editor} className="p-0" />
      </div>
      <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 border-t border-gray-100">
        <span className="text-[10px] text-gray-400">@ to mention</span>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={editor.isEmpty}
          className="h-6 px-2.5 text-[10px] font-medium bg-zinc-900 text-white hover:bg-zinc-800 rounded transition-colors shadow-none"
        >
          Post
        </Button>
      </div>
    </div>
  );
};
